import { Segmented } from 'antd'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { useHasWorkspacePermissions } from 'src/hooks'
import { WorkspacePermissionCode } from 'src/utils'

import { AuditExportsPanel } from '../../components/AuditExportsPanel'
import { AuditPanelHeader } from '../../components/AuditPanelHeader'
import { WorkspacePageShell, useWorkspaceRoute } from '../WorkspacePageShell'

import { EventsPanel } from './EventsPanel'

const TAB_KEYS = ['events', 'exports'] as const
type TabKey = (typeof TAB_KEYS)[number]

const isTabKey = (value: string | null): value is TabKey =>
  TAB_KEYS.includes(value as TabKey)

export const WorkspaceAuditPage = () => {
  const { t } = useTranslation()
  const { workspaceId, idValid } = useWorkspaceRoute()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  // Exports are their own code: creating, downloading and deleting them all
  // answer to `audit.export`, so a member with only `audit.view` never sees the
  // tab — nor lands on it through a stale `?tab=exports` link.
  const mayExportAudit = useHasWorkspacePermissions(
    WorkspacePermissionCode.AUDIT_EXPORT
  )

  const requestedTab = searchParams.get('tab')
  const tab: TabKey =
    isTabKey(requestedTab) && (requestedTab !== 'exports' || mayExportAudit)
      ? requestedTab
      : 'events'

  // Switching tabs clears the previous tab's filter params — the two panels
  // have different URL schemas (severity/action vs status) and mixing them
  // leaves stale filters on the destination tab.
  const handleTabChange = (nextTab: TabKey) => {
    const nextSearch = nextTab === 'events' ? '' : `?tab=${nextTab}`

    navigate(
      { pathname: location.pathname, search: nextSearch },
      { replace: false }
    )
  }

  const viewSelector = mayExportAudit ? (
    <Segmented<TabKey>
      value={tab}
      onChange={handleTabChange}
      options={[
        { value: 'events', label: t('workspace.auditPage.tabs.events') },
        { value: 'exports', label: t('workspace.auditPage.tabs.exports') }
      ]}
    />
  ) : null

  const header = (
    <AuditPanelHeader
      title={t('workspace.auditPage.sectionTitle')}
      subtitle={t('workspace.auditPage.sectionDescription')}
    />
  )

  return (
    <WorkspacePageShell breadcrumbLeaf={t('workspace.auditPage.sectionTitle')}>
      {tab === 'events' && (
        <EventsPanel
          workspaceId={workspaceId}
          idValid={idValid}
          viewSelector={viewSelector}
          header={header}
        />
      )}
      {tab === 'exports' && mayExportAudit && (
        <AuditExportsPanel
          workspaceId={workspaceId}
          idValid={idValid}
          viewSelector={viewSelector}
          header={header}
        />
      )}
    </WorkspacePageShell>
  )
}
